import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging'

import app, { httpsCallable } from './app'
import config from './firebaseConfig'
import { isNativePlatform } from '../platform/runtime'

const LOCAL_STORAGE_TOKEN_KEY = 'employer-fcm-token'

function getStoredToken() {
  try {
    return window.localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY)
  } catch {
    return null
  }
}

function storeToken(token) {
  try {
    if (token) {
      window.localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token)
    } else {
      window.localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY)
    }
  } catch {
    // localStorage not available
  }
}

function canUseMessagingBrowserApis() {
  // #1690 (spec addendum §5.2): Web Push/VAPID is a web-only capability path —
  // a native build must go through the Push adapter (src/services/platform/adapters/push.js)
  // instead, never fall into this browser API path just because Capacitor's
  // WebView also happens to report a secure context / localhost.
  return !isNativePlatform() &&
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'Notification' in window &&
    (window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname))
}

/**
 * Dedicated scope for the messaging service worker.
 *
 * THIS IS LOAD-BEARING. A service worker registration is keyed by SCOPE, and
 * registering a different script at a scope REPLACES whatever was there. The app
 * registers /service-worker.js at "/" (src/index.js); this used to register
 * /firebase-messaging-sw.js at "/" as well, so on every page load the two
 * evicted each other. Two symptoms, one cause:
 *
 *   - The push subscription belongs to the registration, so every eviction
 *     destroyed it and getToken minted a fresh token. Tokens churned on every
 *     load, and any token more than a moment old came back "Device
 *     unregistered".
 *   - When a push arrived while /service-worker.js happened to be the active
 *     worker — it has no push handler — Chrome fell back to its own generic
 *     notification: the site URL, with no title, body or icon. Which is exactly
 *     what users saw, whatever the payload contained.
 *
 * This is the scope the Firebase SDK itself uses by default.
 */
const MESSAGING_SW_SCOPE = '/firebase-cloud-messaging-push-scope'

/**
 * Remove a messaging worker left registered at "/" by the previous behaviour.
 * Without this, existing clients keep the colliding registration forever.
 *
 * @return {Promise<number>} How many stale registrations were removed.
 */
export async function unregisterCollidingMessagingWorker() {
  let removed = 0
  try {
    for (const registration of await navigator.serviceWorker.getRegistrations()) {
      const script = registration.active?.scriptURL ||
        registration.installing?.scriptURL ||
        registration.waiting?.scriptURL || ''
      if (script.includes('firebase-messaging-sw') &&
          !String(registration.scope || '').includes(MESSAGING_SW_SCOPE)) {
        await registration.unregister()
        removed += 1
      }
    }
  } catch (error) {
    // Best-effort cleanup; the scoped registration below is what matters.
  }
  return removed
}

export async function registerMessagingServiceWorker() {
  if (!canUseMessagingBrowserApis()) {
    return null
  }

  await unregisterCollidingMessagingWorker()

  return navigator.serviceWorker.register(
    `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`,
    { scope: MESSAGING_SW_SCOPE },
  )
}

async function unregisterStoredTokenIfNeeded() {
  const existingToken = getStoredToken()
  if (!existingToken) {
    return
  }

  try {
    await httpsCallable('unregisterEmployerMessagingToken')({ token: existingToken })
  } catch (error) {
    console.warn('Failed to unregister employer messaging token:', error)
  }

  storeToken(null)
}

/**
 * Every reason push can be unavailable, and whether the user can still fix it
 * from inside the app.
 *
 * These states already existed as return values of the registration below — they
 * were simply never surfaced, which is why push being completely dead in
 * production went unnoticed by everyone including us.
 */
export const PUSH_STATUS = {
  /** Never asked. One tap fixes it. */
  PROMPTABLE: 'promptable',
  /** Asked and refused. Only site settings can undo it. */
  BLOCKED: 'blocked',
  /** iOS Safari outside an installed PWA, or a browser without push. */
  UNSUPPORTED: 'unsupported',
  /** Permission granted but no token — a real fault, not a user choice. */
  BROKEN: 'broken',
  READY: 'ready',
}

/**
 * Read the current push state WITHOUT prompting.
 *
 * `Notification.permission` is readable synchronously and triggers nothing, so
 * this is safe to call on every render.
 *
 * @return {string} one of PUSH_STATUS
 */
export function readPushPermission() {
  if (!canUseMessagingBrowserApis()) {
    return PUSH_STATUS.UNSUPPORTED
  }

  switch (Notification.permission) {
    case 'granted':
      // Already working — never nag, whatever the browser.
      return PUSH_STATUS.READY
    case 'denied':
      return PUSH_STATUS.BLOCKED
    default:
      return PUSH_STATUS.PROMPTABLE
  }
}

/**
 * Watch for the user changing the permission in browser settings, so a warning
 * clears itself the moment it stops being true — no reload, no stale banner.
 *
 * @param {Function} onChange called with a PUSH_STATUS
 * @return {Function} unsubscribe
 */
export function watchPushPermission(onChange) {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return () => {}
  }

  let status = null
  const handler = () => onChange(readPushPermission())

  navigator.permissions
    .query({ name: 'notifications' })
    .then((permissionStatus) => {
      status = permissionStatus
      status.addEventListener('change', handler)
    })
    .catch(() => {
      // Firefox and older Safari reject this query; the state is still readable,
      // it just will not update live.
    })

  return () => {
    if (status) status.removeEventListener('change', handler)
  }
}

/**
 * Map a registration result onto a user-facing state.
 *
 * `missing-vapid-key` and `token-unavailable` are BROKEN rather than a user
 * choice: the user did nothing wrong and cannot fix them from settings.
 *
 * @param {object} result from registerEmployerPushNotifications
 * @return {string} one of PUSH_STATUS
 */
export function statusFromRegistration(result) {
  switch (result?.status) {
    case 'registered':
      return PUSH_STATUS.READY
    case 'unsupported':
      return PUSH_STATUS.UNSUPPORTED
    case 'permission-denied':
      return PUSH_STATUS.BLOCKED
    case 'missing-vapid-key':
    case 'token-unavailable':
      return PUSH_STATUS.BROKEN
    default:
      return readPushPermission()
  }
}

/**
 * Presentation for a notification we display ourselves. Mirrors the `webpush`
 * block the server sends (functions/notifications/pushMessaging.js) so a
 * notification looks identical whether the app was open or closed.
 *
 * @param {object} payload FCM message payload.
 * @return {{title: string, options: object}}
 */
export function buildForegroundNotification(payload) {
  const title = payload?.notification?.title || 'התראה חדשה'
  const data = payload?.data || {}

  return {
    title,
    options: {
      body: payload?.notification?.body || '',
      icon: '/logo192.png',
      badge: '/logo192.png',
      lang: 'he',
      dir: 'rtl',
      // Same collapsing rule as the server's webpush.notification.tag.
      tag: data.type || 'kravpayroll',
      // The service worker's notificationclick listener reads data.route —
      // and because WE display this one, that listener finally runs.
      data,
    },
  }
}

/**
 * Show a system notification even while the app is open.
 *
 * FCM does NOT display a notification when the page is focused: it hands the
 * payload to onMessage and expects the app to decide. There was no onMessage
 * handler, so every notification that arrived while someone was using the app
 * was received and silently dropped — which is the common case, since people
 * are usually in the app when their request gets approved.
 *
 * Displaying through the service worker registration (rather than `new
 * Notification()`) is what makes it a real system notification, and what lets
 * the existing notificationclick handler route the tap.
 *
 * @return {Promise<Function>} unsubscribe
 */
export async function initForegroundPushDisplay() {
  if (!canUseMessagingBrowserApis() || !(await isSupported())) {
    return () => {}
  }

  const registration = await navigator.serviceWorker.getRegistration(MESSAGING_SW_SCOPE) ||
    await registerMessagingServiceWorker()

  if (!registration) {
    return () => {}
  }

  return onMessage(getMessaging(app), (payload) => {
    if (Notification.permission !== 'granted') {
      return
    }
    const { title, options } = buildForegroundNotification(payload)
    registration.showNotification(title, options)
  })
}

export async function registerEmployerPushNotifications() {
  if (!canUseMessagingBrowserApis()) {
    return { status: 'unsupported' }
  }

  if (!(await isSupported())) {
    return { status: 'unsupported' }
  }

  const registration = await registerMessagingServiceWorker()
  // Production keeps its key in the gitignored `.env.production`. Staging's
  // lives in `config.staging.json` instead (#183): a VAPID *public* key ships
  // inside every bundle anyway, and the App Check site key next door is
  // inlined for exactly the same reason — a gitignored file is one more thing
  // a worktree build silently does without.
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || config.vapidKey

  if (!vapidKey) {
    return { status: 'missing-vapid-key', registration }
  }

  let permission = Notification.permission
  if (permission === 'default') {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    await unregisterStoredTokenIfNeeded()
    return { status: 'permission-denied', registration }
  }

  const messaging = getMessaging(app)
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  })

  if (!token) {
    await unregisterStoredTokenIfNeeded()
    return { status: 'token-unavailable', registration }
  }

  const previousToken = getStoredToken()
  if (previousToken && previousToken !== token) {
    await unregisterStoredTokenIfNeeded()
  }

  await httpsCallable('registerEmployerMessagingToken')({ token })
  storeToken(token)

  return {
    status: 'registered',
    token,
    registration,
  }
}